import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, trackScrollDepth, trackExit, resetPageTimer, isTrackingAllowed } from "@/lib/analytics";
import { gaTrackPageView } from "@/lib/ga";

export const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    resetPageTimer();

    const scheduleTrack = () => {
      if (!isTrackingAllowed()) return;
      const send = () => {
        trackPageView();
        gaTrackPageView(location.pathname + location.search);
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(send, { timeout: 3000 });
      } else {
        setTimeout(send, 2000);
      }
    };

    scheduleTrack();

    const handleConsentChange = (e: CustomEvent) => {
      if (e.detail === "accepted") {
        trackPageView();
        gaTrackPageView(location.pathname + location.search);
      }
    };

    window.addEventListener("cookieConsentChanged", handleConsentChange as EventListener);

    return () => {
      window.removeEventListener("cookieConsentChanged", handleConsentChange as EventListener);
    };
  }, [location]);

  // Scroll depth tracking
  useEffect(() => {
    if (!isTrackingAllowed()) return;

    let scrollTimer: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        requestAnimationFrame(() => trackScrollDepth());
      }, 500);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimer);
    };
  }, []);

  // Exit tracking
  useEffect(() => {
    if (!isTrackingAllowed()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        trackExit();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};
